/* eslint react/require-default-props: 0 */

import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { setHelmetTags } from 'helpers/HelmetHelper';
import { SITE_PLATFORM_WEB } from 'helpers/BreakpointHelper';
import { getRecommendationId, setSegmentContentPersonality } from '@foxcorp/delta-web-analytics';
import Breadcrumbs from 'components/Breadcrumbs';
import { Hero, AUTH_SERIES } from 'componentsV2/Hero';
import { isTypeSeason, PANEL_RECOMMENDED_SERIES } from 'helpers/ContentHelper';
import Schema from 'componentsV2/Schema';
import { SCHEMA_TYPES } from 'componentsV2/Schema/SchemaHelper';
import CarouselSection from './CarouselSection';
import { overrideMetatags } from '../ContentDetailHelper';
import About from './About';
import Recommended from './Recommended';
import TabListSeasons from './Tablist/Tablist';
import SeriesDetailStyle from '../styles/SeriesDetail.module.scss';

const FullDetailView = ({
  accessToken,
  account,
  bookmarks,
  breadcrumbsData = null,
  breakpoint = SITE_PLATFORM_WEB,
  continueWatchingItems = [],
  deviceId,
  disableScroll,
  favoritableItems = [],
  favoritesManager = {},
  inline = {},
  isAnonymous = false,
  isAuthenticated,
  isPersonality = false,
  overlay,
  pageViewEvent,
  params: { showId, pageType, pageId },
  preauthorizedResources,
  profileId,
  recommendationIdData = null,
  recommended = null,
  seo = {},
  seriesData = {},
  seriesType,
  setOverlay,
  tabIndex,
  tabs: sections = [],
}) => {
  const item = _.get(inline, 'items.member.0', null);
  const recommendationId = getRecommendationId(recommendationIdData);
  const panelProps = {
    breakpoint,
    collectionCount: sections.length,
    inline,
    seriesType,
  };
  const aboutProps = {
    breakpoint,
    description: seriesData?.longDescription ?? '',
    image: seriesData?.images?.seriesList?.raw ?? null,
    name: seriesData?.name ?? '',
  };
  const isSeasonAsc = seriesData?.orderSeasons === 'asc';
  const tabs = sections?.filter(section => {
    const sectionType = section?.['@type'] ?? null;
    return isTypeSeason(sectionType);
  });

  const tablistProps = {
    accessToken,
    collectionCount: sections.length,
    bookmarks,
    tabs: isSeasonAsc ? tabs?.reverse() : tabs,
    tabIndex,
    inline,
    setOverlay,
    breakpoint,
    overlay,
    seriesData,
    isSeriesDetailPage: true,
    showId,
    pageId,
    pageType,
    pageViewEvent,
    isAuthenticated,
    preauthorizedResources,
    utagSeriesType: seriesType,
    verticalPosition: 2,
  };

  const contentPersonality = _.get(seriesData, 'categoryTags');

  if (contentPersonality) setSegmentContentPersonality(contentPersonality);
  const recommendedProps = {
    accessToken,
    breakpoint,
    collectionCount: sections.length,
    deviceId,
    overrideAdditionalPosterStyle: true,
    pageType,
    recommended,
    seriesType,
    verticalPosition: 4,
  };

  let schemaType;
  let schemaItem = seriesData;

  switch (seriesType) {
    case 'movie':
      schemaType = SCHEMA_TYPES.MOVIES;
      schemaItem = seriesData?.latestMovie;
      break;
    case 'special':
      schemaType = SCHEMA_TYPES.MOVIES;
      schemaItem = seriesData?.latestEpisode;
      break;
    case 'show':
    case 'series':
      schemaType = SCHEMA_TYPES.SERIES;
      break;
    default:
      break;
  }
  if (isPersonality) schemaType = SCHEMA_TYPES.PERSON;
  const schema = [{ item: schemaItem, type: schemaType }];

  return (
    <>
      {setHelmetTags(seo)}
      <Schema schemas={schema} />
      {overrideMetatags({ seriesData, isPersonality })}
      <Hero
        account={account}
        accessToken={accessToken}
        bookmarks={bookmarks}
        continueWatchingItems={continueWatchingItems}
        disableScroll={disableScroll}
        favoriteProps={{ favoritesManager, favoritableItems, recommendationId }}
        item={item}
        panel={seriesData}
        profileId={profileId}
        type={AUTH_SERIES}
      />
      {sections && <TabListSeasons {...tablistProps} />}
      {sections &&
        sections.map(panel => {
          if (!panel) return null;
          const displayTemplate = _.get(panel, 'displayTemplate');
          const isClips = _.get(panel, 'videoTypes', []).includes('clip');
          const isRecommended = panel?.headline?.toLowerCase() === PANEL_RECOMMENDED_SERIES;
          const isPosters = displayTemplate === 'featured' || isRecommended;
          if (isClips || isRecommended) {
            return CarouselSection({
              bookmarks,
              isAnonymous,
              isPosters,
              renderDivider: true,
              panel,
              verticalPosition: 3,
              ...panelProps,
            });
          }
          return null;
        })}
      <Recommended {...recommendedProps} />
      {aboutProps.image && <About {...aboutProps} />}
      <Breadcrumbs data={breadcrumbsData} customStyle={SeriesDetailStyle.breadcrumbs} />
    </>
  );
};

FullDetailView.propTypes = {
  accessToken: PropTypes.string,
  account: PropTypes.shape({}),
  bookmarks: PropTypes.arrayOf(PropTypes.shape({})),
  breadcrumbsData: PropTypes.arrayOf(PropTypes.shape({})),
  breakpoint: PropTypes.string.isRequired,
  continueWatchingItems: PropTypes.arrayOf(PropTypes.shape([])),
  deviceId: PropTypes.string,
  disableScroll: PropTypes.func.isRequired,
  favoritableItems: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  favoritesManager: PropTypes.shape({}).isRequired,
  inline: PropTypes.shape({}).isRequired,
  isAnonymous: PropTypes.bool.isRequired,
  isAuthenticated: PropTypes.bool,
  isPersonality: PropTypes.bool,
  overlay: PropTypes.string,
  pageViewEvent: PropTypes.func,
  params: PropTypes.shape({}),
  preauthorizedResources: PropTypes.arrayOf(PropTypes.string),
  profileId: PropTypes.string,
  recommendationIdData: PropTypes.oneOfType([PropTypes.node, PropTypes.shape({})]),
  recommended: PropTypes.shape({}),
  renderTabs: PropTypes.func,
  seo: PropTypes.shape({}),
  seriesData: PropTypes.shape({}).isRequired,
  seriesType: PropTypes.string,
  setIsBrandingClicked: PropTypes.func,
  setOverlay: PropTypes.func.isRequired,
  subTabIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  tabIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  tabs: PropTypes.arrayOf(PropTypes.shape([])).isRequired,
};

export default FullDetailView;
